import * as Y from 'yjs';
import {
    DataValue,
    isShapeValue,
    isFunctionReferenceValue,
    isSecretReferenceValue,
    ShapeValue,
    FunctionReferenceValue,
    SecretReferenceValue,
} from '@glueapp/graph-types';

/**
 * Encode/decode for `DataValue` into Y.Map wrappers.
 *
 * Every encoded value is a Y.Map with a `kind` discriminator. This gives:
 *   - LWW on primitive leaves                                  (kind: 'primitive')
 *   - Recursive per-key merge for plain objects                 (kind: 'object')
 *   - Order-preserving merge for arrays                          (kind: 'array')
 *   - Opaque LWW for tagged sentinels (Shape/FunctionRef/SecretRef)
 *
 * Two users editing different keys of the same object value converge non-destructively;
 * two users editing the same primitive leaf clobber each other (acceptable for v1 —
 * surface the conflict via `currentlyEditingPort` awareness).
 */

const KIND = 'kind';
const VALUE = 'value';
const ENTRIES = 'entries';
const JSON_PAYLOAD = 'json';
const FUNCTION_ID = 'functionId';
const SECRET_NAME = 'secretName';

export type DataValueKind = 'primitive' | 'object' | 'array' | 'shape' | 'functionRef' | 'secretRef';

export function encodeDataValue(value: DataValue): Y.Map<unknown> {
    const wrapper = new Y.Map<unknown>();
    if (value === null || typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
        wrapper.set(KIND, 'primitive' satisfies DataValueKind);
        wrapper.set(VALUE, value);
        return wrapper;
    }
    if (Array.isArray(value)) {
        wrapper.set(KIND, 'array' satisfies DataValueKind);
        const entries = new Y.Array<Y.Map<unknown>>();
        entries.push(value.map(encodeDataValue));
        wrapper.set(ENTRIES, entries);
        return wrapper;
    }
    if (isShapeValue(value)) {
        wrapper.set(KIND, 'shape' satisfies DataValueKind);
        wrapper.set(JSON_PAYLOAD, JSON.stringify(value));
        return wrapper;
    }
    if (isFunctionReferenceValue(value)) {
        wrapper.set(KIND, 'functionRef' satisfies DataValueKind);
        wrapper.set(FUNCTION_ID, value.functionId);
        return wrapper;
    }
    if (isSecretReferenceValue(value)) {
        wrapper.set(KIND, 'secretRef' satisfies DataValueKind);
        wrapper.set(SECRET_NAME, value.secretName);
        return wrapper;
    }
    // Plain object
    wrapper.set(KIND, 'object' satisfies DataValueKind);
    const entries = new Y.Map<Y.Map<unknown>>();
    for (const [key, entry] of Object.entries(value)) {
        if (entry === undefined) continue;
        entries.set(key, encodeDataValue(entry));
    }
    wrapper.set(ENTRIES, entries);
    return wrapper;
}

export function decodeDataValue(wrapper: Y.Map<unknown>): DataValue {
    const kind = wrapper.get(KIND) as DataValueKind | undefined;
    switch (kind) {
        case 'primitive':
            return wrapper.get(VALUE) as number | string | boolean | null;
        case 'array': {
            const entries = wrapper.get(ENTRIES) as Y.Array<Y.Map<unknown>>;
            const length = entries.length;
            const result: DataValue[] = new Array(length);
            for (let index = 0; index < length; index++) {
                result[index] = decodeDataValue(entries.get(index));
            }
            return result;
        }
        case 'object': {
            const entries = wrapper.get(ENTRIES) as Y.Map<Y.Map<unknown>>;
            const result: { [key: string]: DataValue } = {};
            for (const [key, entry] of entries.entries()) {
                result[key] = decodeDataValue(entry);
            }
            return result;
        }
        case 'shape':
            return JSON.parse(wrapper.get(JSON_PAYLOAD) as string) as ShapeValue;
        case 'functionRef':
            return {
                isFunctionReferenceValue: true,
                functionId: wrapper.get(FUNCTION_ID) as string,
            } satisfies FunctionReferenceValue;
        case 'secretRef':
            return {
                isSecretReferenceValue: true,
                secretName: wrapper.get(SECRET_NAME) as string,
            } satisfies SecretReferenceValue;
        default:
            throw new Error(`decodeDataValue: unknown wrapper kind: ${String(kind)}`);
    }
}

/**
 * Encode a `{ [portName]: DataValue }` map into the parent Y.Map, replacing all
 * existing entries. Used by reducer actions that overwrite the full port-value
 * bag on a node that may already have values. Requires `target` to already be
 * integrated into a Y.Doc so the clear pass can read existing keys.
 */
export function replaceEncodedDataValueMap(target: Y.Map<Y.Map<unknown>>, source: { [key: string]: DataValue }): void {
    for (const existingKey of Array.from(target.keys())) {
        target.delete(existingKey);
    }
    fillEncodedDataValueMap(target, source);
}

/**
 * Insert encoded entries without clearing the target first. Use this at
 * hydration time when the target is freshly constructed and empty, since
 * reading `.keys()` on an unintegrated Y.Map emits a yjs warning.
 */
export function fillEncodedDataValueMap(target: Y.Map<Y.Map<unknown>>, source: { [key: string]: DataValue }): void {
    for (const [key, value] of Object.entries(source)) {
        if (value === undefined) continue;
        target.set(key, encodeDataValue(value));
    }
}

/**
 * Decode every entry of a `Y.Map<wrapper>` back into a `{ [key]: DataValue }`.
 */
export function decodeDataValueMap(source: Y.Map<Y.Map<unknown>>): { [key: string]: DataValue } {
    const result: { [key: string]: DataValue } = {};
    for (const [key, wrapper] of source.entries()) {
        result[key] = decodeDataValue(wrapper);
    }
    return result;
}
